import { useCallback, useRef, useState } from 'react';
import { commsApi } from '@/utils/commsApi';
import { playIncomingChirp, unlockAudio } from '@/utils/audioCues';

// Radio-style auto-play queue for incoming voice clips.
export function useVoicePlayer({ enabled, userId }) {
  const [playingId, setPlayingId] = useState(null);
  const [soundBlocked, setSoundBlocked] = useState(false);
  const queueRef = useRef([]);
  const busyRef = useRef(false);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const drain = useCallback(async () => {
    if (busyRef.current || queueRef.current.length === 0) return;
    busyRef.current = true;
    const msg = queueRef.current[0];
    const audio = new Audio(commsApi.fileUrl(msg.file_info.file_id));
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      busyRef.current = false;
      setPlayingId(null);
      queueRef.current.shift();
      drain();
    };
    audio.onended = finish;
    audio.onerror = finish;
    try {
      await playIncomingChirp();
      setPlayingId(msg.id);
      await audio.play();
      if (msg.from_user_id !== userId) commsApi.markPlayed(msg.id).catch(() => {});
    } catch (err) {
      if (settled) return;
      if (err?.name === 'NotAllowedError') {
        settled = true;
        busyRef.current = false;
        setPlayingId(null);
        setSoundBlocked(true);
        return;
      }
      finish();
    }
  }, [userId]);

  const enqueue = useCallback((msg) => {
    if (!enabledRef.current) return;
    queueRef.current.push(msg);
    drain();
  }, [drain]);

  const enableSound = useCallback(async () => {
    await unlockAudio();
    setSoundBlocked(false);
    drain();
  }, [drain]);

  return { enqueue, playingId, soundBlocked, enableSound };
}
